import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Gift } from "lucide-react";
import AuthShell, { inputCls } from "@/components/site/AuthShell";
import { useAuth } from "@/components/site/auth";
import { api, formatApiError } from "@/lib/api";
import { PACKAGES, inr } from "@/data/content";

export default function Register() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || localStorage.getItem("ilmora-ref") || "");
  const [referrer, setReferrer] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", package: params.get("package") || "ilmora-prime" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ref) { setReferrer(null); return; }
    const t = setTimeout(() => api.get(`/referral/${ref}`).then((r) => setReferrer(r.data)).catch(() => setReferrer(false)), 300);
    return () => clearTimeout(t);
  }, [ref]);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register({ ...form, ref: ref.trim() || null });
      localStorage.removeItem("ilmora-ref");
      nav("/dashboard");
    } catch (e2) { setErr(formatApiError(e2)); } finally { setLoading(false); }
  };

  return (
    <AuthShell testid="register-page" title="Create your" highlight="ID" sub="Join Ilmora. Your ID becomes active once our team approves it.">
      {referrer && (
        <div data-testid="referrer-banner" className="glass rounded-2xl p-3 mb-4 flex items-center gap-3 text-sm">
          <Gift size={18} className="text-[var(--pink)]" /> Invited by <b>{referrer.name}</b>
        </div>
      )}
      <form onSubmit={submit} className="space-y-3">
        <input required minLength={2} placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="register-name-input" />
        <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="register-email-input" />
        <input required type="tel" minLength={7} placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="register-phone-input" />
        <input required type="password" minLength={6} placeholder="Password (min 6 characters)" value={form.password} onChange={set("password")} className={inputCls} data-testid="register-password-input" />
        <select value={form.package} onChange={set("package")} className={inputCls} data-testid="register-package-select">
          {PACKAGES.map((p) => <option key={p.slug} value={p.slug}>{p.name} · {inr(p.price)}</option>)}
        </select>
        <input placeholder="Referral code (optional)" value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} className={inputCls} data-testid="register-ref-input" />
        {referrer === false && <p data-testid="referral-invalid" className="text-xs text-red-500">This referral code is not valid.</p>}
        {err && <p data-testid="register-error" className="text-sm text-red-500">{err}</p>}
        <button disabled={loading} data-testid="register-submit-btn" className="btn-neon w-full justify-center disabled:opacity-70">{loading && <Loader2 className="animate-spin" size={18} />} Create ID</button>
      </form>
      <p className="text-sm text-center mt-6 text-[var(--muted)]">Already have an ID? <Link to="/login" data-testid="go-login-link" className="text-[var(--pink)] font-semibold">Sign In</Link></p>
    </AuthShell>
  );
}
